import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { PureComponent } from 'react';
import { ActionCreators, } from '@redux-devtools/core';
import { createInspectorMonitorThemeFromBase16Theme, resolveBase16Theme, } from './utils/themes.js';
import ActionList from './ActionList.js';
import ActionPreview from './ActionPreview.js';
import getInspectedState from './utils/getInspectedState.js';
import createDiffPatcher from './createDiffPatcher.js';
import { reducer, updateMonitorState, } from './redux.js';
import { ThemeProvider } from '@emotion/react';
const { commit, sweep, toggleAction, jumpToAction, jumpToState, reorderAction, } = ActionCreators;
function getLastActionId(props) {
    return props.stagedActionIds[props.stagedActionIds.length - 1];
}
function getCurrentActionId(props, monitorState) {
    return monitorState.selectedActionId === null
        ? props.stagedActionIds[props.currentStateIndex]
        : monitorState.selectedActionId;
}
function getFromState(actionIndex, stagedActionIds, computedStates, monitorState) {
    const { startActionId } = monitorState;
    if (startActionId === null) {
        return actionIndex > 0 ? computedStates[actionIndex - 1] : null;
    }
    let fromStateIdx = stagedActionIds.indexOf(startActionId - 1);
    if (fromStateIdx === -1)
        fromStateIdx = 0;
    return computedStates[fromStateIdx];
}
function createIntermediateState(props, monitorState) {
    const { supportImmutable, computedStates, stagedActionIds, actionsById: actions, diffObjectHash, diffPropertyFilter, } = props;
    const { inspectedStatePath, inspectedActionPath } = monitorState;
    const currentActionId = getCurrentActionId(props, monitorState);
    const currentAction = actions[currentActionId] && actions[currentActionId].action;
    const actionIndex = stagedActionIds.indexOf(currentActionId);
    const fromState = getFromState(actionIndex, stagedActionIds, computedStates, monitorState);
    const toState = computedStates[actionIndex];
    const error = toState && toState.error;
    const fromInspectedState = !error &&
        fromState &&
        getInspectedState(fromState.state, inspectedStatePath, supportImmutable);
    const toInspectedState = !error &&
        toState &&
        getInspectedState(toState.state, inspectedStatePath, supportImmutable);
    const delta = !error &&
        fromState &&
        toState &&
        createDiffPatcher(diffObjectHash, diffPropertyFilter).diff(fromInspectedState, toInspectedState);
    return {
        delta,
        nextState: toState && getInspectedState(toState.state, inspectedStatePath, false),
        action: getInspectedState(currentAction, inspectedActionPath, false),
        error,
    };
}
class DevtoolsInspector extends PureComponent {
    constructor() {
        super(...arguments);
        this.state = {
            ...createIntermediateState(this.props, this.props.monitorState),
            isWideLayout: false,
        };
        this.updateMonitorState = (monitorState) => {
            this.props.dispatch(updateMonitorState(monitorState));
        };
        this.inspectorCreateRef = (node) => {
            this.inspectorRef = node;
        };
        this.handleToggleAction = (actionId) => {
            this.props.dispatch(toggleAction(actionId));
        };
        this.handleJumpToState = (actionId) => {
            if (jumpToAction) {
                this.props.dispatch(jumpToAction(actionId));
            }
            else {
                // Fallback for redux-devtools-instrument < 1.5
                const index = this.props.stagedActionIds.indexOf(actionId);
                if (index !== -1)
                    this.props.dispatch(jumpToState(index));
            }
        };
        this.handleReorderAction = (actionId, beforeActionId) => {
            if (reorderAction)
                this.props.dispatch(reorderAction(actionId, beforeActionId));
        };
        this.handleCommit = () => {
            this.props.dispatch(commit());
        };
        this.handleSweep = () => {
            this.props.dispatch(sweep());
        };
        this.handleSearch = (val) => {
            this.updateMonitorState({ searchValue: val });
        };
        this.handleSelectAction = (e, actionId) => {
            const { monitorState } = this.props;
            let startActionId;
            let selectedActionId;
            if (e.shiftKey && monitorState.selectedActionId !== null) {
                if (monitorState.startActionId !== null) {
                    if (actionId >= monitorState.startActionId) {
                        startActionId = Math.min(monitorState.startActionId, monitorState.selectedActionId);
                        selectedActionId = actionId;
                    }
                    else {
                        selectedActionId = Math.max(monitorState.startActionId, monitorState.selectedActionId);
                        startActionId = actionId;
                    }
                }
                else {
                    startActionId = Math.min(actionId, monitorState.selectedActionId);
                    selectedActionId = Math.max(actionId, monitorState.selectedActionId);
                }
            }
            else {
                startActionId = null;
                if (actionId === monitorState.selectedActionId ||
                    monitorState.startActionId !== null) {
                    selectedActionId = null;
                }
                else {
                    selectedActionId = actionId;
                }
            }
            this.updateMonitorState({ startActionId, selectedActionId });
        };
        this.handleInspectPath = (pathType, path) => {
            this.updateMonitorState({ [pathType]: path });
        };
        this.handleSelectTab = (tabName) => {
            this.updateMonitorState({ tabName });
        };
    }
    componentDidMount() {
        this.updateSizeMode();
        this.updateSizeTimeout = window.setInterval(this.updateSizeMode.bind(this), 150);
    }
    componentWillUnmount() {
        clearTimeout(this.updateSizeTimeout);
    }
    updateSizeMode() {
        const isWideLayout = this.inspectorRef.offsetWidth > 500;
        if (isWideLayout !== this.state.isWideLayout) {
            this.setState({ isWideLayout });
        }
    }
    UNSAFE_componentWillReceiveProps(nextProps) {
        const nextMonitorState = nextProps.monitorState;
        const monitorState = this.props.monitorState;
        if (getCurrentActionId(this.props, monitorState) !==
            getCurrentActionId(nextProps, nextMonitorState) ||
            monitorState.startActionId !== nextMonitorState.startActionId ||
            monitorState.inspectedStatePath !== nextMonitorState.inspectedStatePath ||
            monitorState.inspectedActionPath !==
                nextMonitorState.inspectedActionPath ||
            this.props.computedStates !== nextProps.computedStates ||
            this.props.stagedActionIds !== nextProps.stagedActionIds) {
            this.setState(createIntermediateState(nextProps, nextMonitorState));
        }
    }
    render() {
        const { stagedActionIds: actionIds, actionsById: actions, computedStates, draggableActions, tabs, theme, invertTheme, skippedActionIds, currentStateIndex, monitorState, dataTypeKey, hideMainButtons, hideActionButtons, sortStateTreeAlphabetically, disableStateTreeCollection, } = this.props;
        const { selectedActionId, startActionId, searchValue, tabName } = monitorState;
        const inspectedPathType = tabName === 'Action' ? 'inspectedActionPath' : 'inspectedStatePath';
        const { isWideLayout, action, nextState, delta, error } = this.state;
        const base16Theme = resolveBase16Theme(theme);
        const inspectorMonitorTheme = createInspectorMonitorThemeFromBase16Theme(base16Theme, invertTheme);
        return (_jsx(ThemeProvider, { theme: inspectorMonitorTheme, children: _jsxs("div", { "data-testid": "inspector", ref: this.inspectorCreateRef, css: [
                    (theme) => ({
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        height: '100%',
                        fontFamily: 'monaco, Consolas, "Lucida Console", monospace',
                        fontSize: '12px',
                        WebkitFontSmoothing: 'antialiased',
                        lineHeight: '1.5em',
                        backgroundColor: theme.BACKGROUND_COLOR,
                        color: theme.TEXT_COLOR,
                    }),
                    isWideLayout && { flexDirection: 'row' },
                ], children: [_jsx(ActionList, { actions,
                        actionIds,
                        isWideLayout,
                        searchValue,
                        selectedActionId,
                        startActionId,
                        skippedActionIds,
                        draggableActions,
                        hideMainButtons,
                        hideActionButtons, onSearch: this.handleSearch, onSelect: this.handleSelectAction, onToggleAction: this.handleToggleAction, onJumpToState: this.handleJumpToState, onCommit: this.handleCommit, onSweep: this.handleSweep, onReorderAction: this.handleReorderAction, currentActionId: actionIds[currentStateIndex], lastActionId: getLastActionId(this.props) }), _jsx(ActionPreview, { base16Theme,
                        invertTheme,
                        isWideLayout,
                        tabs,
                        tabName,
                        delta,
                        error,
                        nextState,
                        computedStates,
                        action,
                        actions,
                        selectedActionId,
                        startActionId,
                        dataTypeKey,
                        sortStateTreeAlphabetically,
                        disableStateTreeCollection, monitorState: this.props.monitorState, updateMonitorState: this.updateMonitorState, onInspectPath: (path) => this.handleInspectPath(inspectedPathType, path), inspectedPath: monitorState[inspectedPathType], onSelectTab: this.handleSelectTab })] }, "inspector") }));
    }
}
DevtoolsInspector.update = reducer;
DevtoolsInspector.defaultProps = {
    select: (state) => state,
    supportImmutable: false,
    draggableActions: true,
    theme: 'inspector',
    invertTheme: true,
};
export default DevtoolsInspector;
